/**
 * AI Captain Chain Analyzer
 * --------------------------
 * Upload a screenshot of your FIFA World Cup 2026 Fantasy team (or enter
 * your XI manually) → backend extracts the lineup with Gemini 3 Pro Vision,
 * matches it against the WC Fantasy pool, builds the day-by-day Captain Chain
 * (A / B / C tiers, hold/switch thresholds, contextual rationale).
 */
import React, { useState, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
    UploadSimple, ImageSquare, Crown, Lightning, ArrowRight, ArrowDown, Spinner, X,
    PencilSimpleLine, CheckCircle, ArrowsClockwise, Info, Trophy, Robot,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtKick(iso) {
    if (!iso) return "TBD";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

const TIER_STYLE = {
    A: { label: "TIER A", text: "text-fa-yellow", border: "border-fa-yellow", bg: "bg-fa-yellow/5" },
    B: { label: "TIER B", text: "text-fa-mint",   border: "border-fa-mint",   bg: "bg-fa-mint/5" },
    C: { label: "TIER C", text: "text-fa-sky",    border: "border-fa-sky",    bg: "bg-fa-sky/5" },
    Avoid: { label: "AVOID", text: "text-white/50", border: "border-white/15", bg: "bg-white/[0.02]" },
};

// ----- Strategy explainer + hero captain card -------------------------------

function ChainStrategyBanner() {
    return (
        <details className="border border-fa-yellow/30 bg-fa-yellow/[0.04] open:bg-fa-yellow/5 transition-colors"
                 data-testid="ai-chain-explainer">
            <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center gap-2 hover:bg-fa-yellow/10">
                <Info size={14} weight="fill" className="text-fa-yellow shrink-0" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-fa-yellow">
                    How to play the captain chain
                </span>
                <ArrowDown size={11} weight="bold" className="ml-auto text-fa-yellow/60" />
            </summary>
            <div className="px-4 py-3 border-t border-fa-yellow/20 space-y-2 font-mono text-[11px] text-white/75 leading-relaxed">
                <p>
                    <strong className="text-fa-yellow">The chain = your fallback order.</strong>{" "}
                    Captain the first player on the list (earliest kickoff). If he blanks, manually switch your armband
                    to the next player on the chain <em>before</em> that player&apos;s kickoff.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1">
                    <div className="border border-fa-mint/30 bg-fa-mint/5 p-2 space-y-1">
                        <div className="flex items-center gap-1.5 text-fa-mint font-bold">
                            <CheckCircle size={12} weight="fill" /> HOLD ≥ 8 pts
                        </div>
                        <p className="text-white/65">
                            Locked in. Don&apos;t chase a higher ceiling — the floor already paid off.
                        </p>
                    </div>
                    <div className="border border-fa-cream/40 bg-fa-cream/5 p-2 space-y-1">
                        <div className="flex items-center gap-1.5 text-fa-cream font-bold">
                            <ArrowsClockwise size={12} weight="fill" /> 6–7 pts · think twice
                        </div>
                        <p className="text-white/65">
                            Only switch if a TIER A captain plays next AND your current got &lt;70% of his expected.
                        </p>
                    </div>
                    <div className="border border-red-400/40 bg-red-400/5 p-2 space-y-1">
                        <div className="flex items-center gap-1.5 text-red-300 font-bold">
                            <ArrowRight size={12} weight="bold" /> SWITCH &lt; 5 pts
                        </div>
                        <p className="text-white/65">
                            Blank. Move the armband to the next tile before its kickoff.
                        </p>
                    </div>
                </div>
                <p className="text-white/55 text-[10px] pt-1">
                    <strong className="text-white/70">Rule of thumb:</strong>{" "}
                    SWITCH any time actual is &lt;70% of expected, even at half-time. The chain is your safety net —
                    use it only when reality forces you to.
                </p>
            </div>
        </details>
    );
}

function ChainHero({ chain, recommendations, userPicks }) {
    if (!chain?.length) return null;
    const primary = chain[0];
    const p = primary.player || {};
    const m = primary.match || {};
    const style = TIER_STYLE[primary.tier] || TIER_STYLE.C;
    const userCap = userPicks?.captain;
    const switchHint = userCap && userCap !== recommendations?.best_first;
    return (
        <div className={`border-2 ${style.border} ${style.bg} p-4 md:p-5 relative overflow-hidden`}
             data-testid="ai-chain-hero">
            <div className="absolute top-0 right-0 px-3 py-1 bg-fa-yellow text-black font-mono text-[10px] uppercase tracking-widest font-bold">
                <Crown size={11} weight="fill" className="inline mr-1 mb-0.5" />
                Primary captain
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">
                Start the matchday with
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <h2 className={`font-display font-black text-3xl md:text-4xl uppercase tracking-tight ${style.text}`}
                    data-testid="ai-chain-primary-name">
                    {p.name}
                </h2>
                <span className={`font-mono text-[11px] uppercase tracking-widest px-2 py-0.5 border ${style.border} ${style.text}`}>
                    {style.label}
                </span>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-white/60 mt-1">
                {p.position} · {p.team_name} · vs <span className="text-white">{m.opponent_name}</span>
                {" · "}
                <span className="text-fa-yellow">{fmtKick(m.utc_date)}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/10 font-mono text-[10px] uppercase tracking-widest">
                <div>
                    <div className="text-white/40">Captain score</div>
                    <div className="font-display font-black text-lg text-white tabular-nums">{primary.score}<span className="text-white/30 text-xs">/100</span></div>
                </div>
                <div>
                    <div className="text-white/40">Expected pts</div>
                    <div className="font-display font-black text-lg text-fa-mint tabular-nums">{primary.expected_pts}<span className="text-white/30 text-xs">p</span></div>
                </div>
                <div>
                    <div className="text-white/40">Chain depth</div>
                    <div className="font-display font-black text-lg text-white tabular-nums">{chain.length}<span className="text-white/30 text-xs"> spots</span></div>
                </div>
            </div>
            {switchHint && (
                <div className="mt-3 pt-3 border-t border-white/10 font-mono text-[11px] text-fa-yellow leading-snug"
                     data-testid="ai-hero-switch-hint">
                    <ArrowRight size={11} weight="bold" className="inline mb-0.5" />{" "}
                    You currently captain <strong>{userCap}</strong>. The model prefers{" "}
                    <strong>{recommendations?.best_first}</strong> for first kickoff (earlier match + higher score).
                </div>
            )}
        </div>
    );
}

function ChainTile({ row, idx, total, nextRow }) {
    const style = TIER_STYLE[row.tier] || TIER_STYLE.C;
    const p = row.player || {};
    const m = row.match || {};
    const t = row.thresholds || {};
    const isFirst = idx === 0;
    const isLast = idx === total - 1;
    const nextName = nextRow?.player?.name;

    return (
        <div className={`border-2 ${style.border} ${style.bg} p-3 space-y-2 relative`}
             data-testid={`ai-chain-slot-${idx}`}>
            {/* Big slot number badge top-left */}
            <div className="absolute -top-2 -left-2 w-7 h-7 flex items-center justify-center bg-black border-2 border-current font-display font-black text-sm tabular-nums"
                 style={{ color: style.text.includes("yellow") ? "#FFF3C2" : style.text.includes("mint") ? "#9EE8B4" : style.text.includes("sky") ? "#9DB7F5" : "#888" }}>
                {idx + 1}
            </div>
            <div className="flex items-center justify-between gap-2 pl-4">
                <div className="min-w-0 flex-1">
                    <div className="font-display font-black text-base truncate">
                        {p.name}
                        <span className="text-white/40 font-mono text-[10px] ml-1.5">
                            {p.position} · {p.team_name}
                        </span>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 truncate">
                        vs <span className="text-white">{m.opponent_name}</span> · {fmtKick(m.utc_date)}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <span className={`inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${style.border} ${style.text} font-bold`}>
                        {style.label}
                    </span>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
                        {row.score}/100 · exp {row.expected_pts}p
                    </div>
                </div>
            </div>
            {row.rationale && (
                <p className="font-mono text-[10px] text-white/65 leading-relaxed pl-4">
                    {row.rationale}
                </p>
            )}
            {/* Action prompt — the bit the user reads at half-time */}
            <div className="pl-4 pt-2 border-t border-white/5 space-y-1">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                    {isFirst ? "Start of matchday" : "At his kickoff"}
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-[10px]">
                    <div className="flex items-start gap-1.5">
                        <CheckCircle size={12} weight="fill" className="text-fa-mint shrink-0 mt-0.5" />
                        <div>
                            <div className="text-fa-mint font-bold uppercase tracking-widest text-[9px]">
                                HOLD ≥ {t.hold_at_or_above ?? "—"}p
                            </div>
                            <div className="text-white/55 text-[9px] leading-snug">
                                {isFirst ? "Lock him in — armband stays." : "Stop the chain, you cashed."}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-start gap-1.5">
                        {isLast ? (
                            <X size={12} weight="bold" className="text-red-300 shrink-0 mt-0.5" />
                        ) : (
                            <ArrowsClockwise size={12} weight="fill" className="text-fa-yellow shrink-0 mt-0.5" />
                        )}
                        <div>
                            <div className={`font-bold uppercase tracking-widest text-[9px] ${isLast ? "text-red-300" : "text-fa-yellow"}`}>
                                SWITCH &lt; {t.switch_below ?? "—"}p
                            </div>
                            <div className="text-white/55 text-[9px] leading-snug">
                                {isLast
                                    ? "Last in chain — no fallback, accept the score."
                                    : <>Move armband to <strong className="text-white/80">{nextName}</strong> before kickoff.</>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ManualLineupEntry({ onSubmit, busy }) {
    const [rows, setRows] = useState(() => Array.from({ length: 11 }, () => ({
        name: "", team_name: "", position: "MID", is_captain: false, is_vice: false,
    })));
    const updateRow = (i, patch) => {
        setRows((curr) => curr.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    };
    const setCaptain = (i) => {
        setRows((curr) => curr.map((r, idx) => ({
            ...r, is_captain: idx === i ? !r.is_captain : false,
        })));
    };
    const filledCount = rows.filter((r) => r.name.trim() && r.team_name.trim()).length;
    const submit = () => {
        const filled = rows.filter((r) => r.name.trim() && r.team_name.trim());
        if (filled.length < 5) {
            toast.error("Add at least 5 players with name + country.");
            return;
        }
        onSubmit(filled);
    };
    const posColor = {
        GKP: "border-l-fa-cream", DEF: "border-l-fa-mint",
        MID: "border-l-fa-sky", FWD: "border-l-fa-yellow",
    };
    return (
        <div className="border border-white/10 bg-[#0d0d0d]"
             data-testid="ai-manual-entry">
            <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between gap-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                    <PencilSimpleLine size={11} weight="fill" className="text-fa-yellow" />
                    Enter your XI · name + country required
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 tabular-nums"
                     data-testid="ai-manual-counter">
                    {filledCount} / 11 filled
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 p-2 max-h-[26rem] overflow-y-auto">
                {rows.map((r, i) => (
                    <div key={i}
                         className={`flex items-center gap-1.5 bg-black/30 border-l-2 px-2 py-1 ${posColor[r.position] || "border-l-white/10"}`}
                         data-testid={`ai-manual-row-${i}`}>
                        <span className="font-mono text-[9px] text-white/30 tabular-nums w-4">{i + 1}</span>
                        <input
                            type="text"
                            value={r.name}
                            onChange={(e) => updateRow(i, { name: e.target.value })}
                            placeholder={i === 0 ? "Mbappé" : "Player"}
                            data-testid={`ai-manual-name-${i}`}
                            className="flex-1 min-w-0 bg-transparent border border-white/10 px-2 py-1 font-mono text-[11px] focus:outline-none focus:border-fa-yellow"
                        />
                        <input
                            type="text"
                            value={r.team_name}
                            onChange={(e) => updateRow(i, { team_name: e.target.value })}
                            placeholder={i === 0 ? "France" : "Country"}
                            data-testid={`ai-manual-team-${i}`}
                            className="w-20 bg-transparent border border-white/10 px-2 py-1 font-mono text-[11px] focus:outline-none focus:border-fa-yellow"
                        />
                        <select
                            value={r.position}
                            onChange={(e) => updateRow(i, { position: e.target.value })}
                            data-testid={`ai-manual-pos-${i}`}
                            className="bg-black/40 border border-white/10 px-1 py-1 font-mono text-[10px] focus:outline-none focus:border-fa-yellow"
                        >
                            <option value="GKP">GK</option>
                            <option value="DEF">DEF</option>
                            <option value="MID">MID</option>
                            <option value="FWD">FWD</option>
                        </select>
                        <button
                            type="button"
                            onClick={() => setCaptain(i)}
                            data-testid={`ai-manual-cap-${i}`}
                            title={r.is_captain ? "Captain" : "Set as captain"}
                            className={`w-6 h-6 flex items-center justify-center font-mono text-[10px] font-bold tracking-widest transition-colors ${
                                r.is_captain
                                    ? "bg-fa-yellow text-black"
                                    : "bg-white/[0.04] text-white/30 hover:text-white border border-white/10"
                            }`}
                        >
                            C
                        </button>
                    </div>
                ))}
            </div>
            <div className="border-t border-white/10 p-2">
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy || filledCount < 5}
                    data-testid="ai-manual-submit"
                    className="w-full bg-fa-yellow text-black font-mono text-[11px] uppercase tracking-widest font-bold py-2.5 hover:bg-fa-yellowHover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-opacity"
                >
                    {busy ? <Spinner size={12} weight="bold" className="animate-spin" /> : <Lightning size={12} weight="fill" />}
                    {filledCount < 5
                        ? `Add ${5 - filledCount} more player${5 - filledCount === 1 ? "" : "s"} to analyze`
                        : `Analyze ${filledCount}-player lineup`}
                </button>
            </div>
        </div>
    );
}

function SquadAuditPanel({ audit }) {
    if (!audit?.rows?.length) return null;
    const VERDICT_STYLE = {
        KEEP:   { text: "text-fa-yellow", border: "border-fa-yellow",  bg: "bg-fa-yellow/10" },
        SOLID:  { text: "text-fa-mint",   border: "border-fa-mint",    bg: "bg-fa-mint/10" },
        WATCH:  { text: "text-fa-sky",    border: "border-fa-sky",     bg: "bg-fa-sky/10" },
        ROTATE: { text: "text-fa-cream",  border: "border-fa-cream/60",bg: "bg-fa-cream/5" },
        BENCH:  { text: "text-white/50",  border: "border-white/20",   bg: "bg-white/[0.03]" },
        SELL:   { text: "text-red-300",   border: "border-red-400/40", bg: "bg-red-400/10" },
    };
    const byV = audit.summary?.by_verdict || {};
    return (
        <div className="space-y-3" data-testid="ai-audit">
            <div className="border border-fa-mint/30 bg-fa-mint/5 p-3 space-y-2"
                 data-testid="ai-audit-summary">
                <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                    <Lightning size={11} weight="fill" /> Squad audit · avg {audit.summary?.avg_overall ?? "—"}/100
                </div>
                <p className="font-mono text-[11px] text-white/70 leading-snug">
                    {audit.headline}
                </p>
                <div className="flex flex-wrap gap-1.5 font-mono text-[9px] uppercase tracking-widest"
                     data-testid="ai-audit-counts">
                    {["KEEP","SOLID","WATCH","ROTATE","BENCH","SELL"].map((v) => {
                        const n = byV[v] || 0;
                        if (!n) return null;
                        const st = VERDICT_STYLE[v];
                        return (
                            <span key={v}
                                  className={`px-1.5 py-0.5 border ${st.border} ${st.text}`}
                                  data-testid={`ai-audit-count-${v}`}>
                                {v} {n}
                            </span>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2"
                 data-testid="ai-audit-grid">
                {audit.rows.map((r, i) => {
                    const st = VERDICT_STYLE[r.verdict] || VERDICT_STYLE.ROTATE;
                    const p = r.player || {};
                    const m = r.match;
                    return (
                        <div key={i}
                             className={`border ${st.border} ${st.bg} p-3 space-y-1.5`}
                             data-testid={`ai-audit-row-${p.name}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="font-display font-black text-sm truncate">
                                        {p.name}
                                        {p.is_captain && <span className="text-fa-yellow font-mono text-[9px] ml-1.5">(C)</span>}
                                        {p.is_vice && <span className="text-fa-sky font-mono text-[9px] ml-1.5">(VC)</span>}
                                        <span className="text-white/40 font-mono text-[10px] ml-1.5">
                                            {p.position} · {p.team_name}
                                        </span>
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 truncate">
                                        {m ? `vs ${m.opponent_name} · ${fmtKick(m.utc_date)}` : "No fixture this MD"}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={`inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${st.border} ${st.text}`}>
                                        {r.verdict}
                                    </span>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
                                        {r.overall}/100
                                    </div>
                                </div>
                            </div>
                            {/* Component bars */}
                            <div className="grid grid-cols-5 gap-1 font-mono text-[8px] uppercase tracking-widest text-white/40">
                                {["fixture","minutes","form","value","outlook"].map((k) => {
                                    const v = r.components?.[k] ?? 0;
                                    return (
                                        <div key={k} className="space-y-0.5">
                                            <div className="flex justify-between">
                                                <span>{k.slice(0,3)}</span>
                                                <span className="tabular-nums text-white/60">{Math.round(v)}</span>
                                            </div>
                                            <div className="h-1 bg-white/5">
                                                <div className={`h-full ${st.bg.replace('/10','/40').replace('/5','/30')}`}
                                                     style={{ width: `${Math.max(2, Math.min(100, v))}%`,
                                                              backgroundColor: 'currentColor' }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Top reason */}
                            <p className="font-mono text-[10px] text-white/55 leading-snug">
                                {r.reasons?.[0]} {r.reasons?.[1]}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function WCCaptainChainAI() {
    const [matchday, setMatchday] = useState(2);
    const [mode, setMode] = useState("upload"); // "upload" | "manual"
    const [imagePreview, setImagePreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);

    const reset = () => {
        setImagePreview(null);
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const callApi = async (formData) => {
        setBusy(true);
        setResult(null);
        try {
            const r = await axios.post(`${API}/worldcup/captain-chain/analyze`, formData, {
                headers: formData instanceof FormData ? { "Content-Type": "multipart/form-data" } : { "Content-Type": "application/json" },
                timeout: 90000,
            });
            setResult(r.data);
            if (r.data?.mapping?.unmatched?.length > 0) {
                toast.warning(`${r.data.mapping.unmatched.length} player(s) not matched`);
            } else {
                toast.success("Captain chain ready");
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Analysis failed");
        } finally {
            setBusy(false);
        }
    };

    const handleFile = (file) => {
        if (!file) return;
        if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
            toast.error("Please upload a PNG, JPEG or WEBP image");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image too large (max 8 MB)");
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);
        const fd = new FormData();
        fd.append("image", file);
        fd.append("matchday", String(matchday));
        fd.append("save", "true");
        callApi(fd);
    };

    const handleManual = (players) => {
        callApi({ matchday, players });
    };

    return (
        <div className="space-y-4" data-testid="wc-ai-captain-chain">
            {/* ── Intro hero ─────────────────────────────────────────────── */}
            <div className="border-2 border-fa-yellow/40 bg-gradient-to-br from-fa-yellow/10 via-transparent to-fa-mint/5 p-4 md:p-5"
                 data-testid="ai-intro-hero">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                    <Robot size={11} weight="fill" />
                    AI Captain Analyzer
                </div>
                <h2 className="font-display font-black text-xl md:text-2xl uppercase tracking-tight mt-1">
                    Drop your team.<br />
                    <span className="text-fa-yellow">Get a day-by-day captain chain</span>{" "}
                    <span className="text-white/40 text-base">+ squad audit</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 font-mono text-[10px] uppercase tracking-widest"
                     data-testid="ai-how-it-works">
                    <div className="flex items-center gap-1.5 text-white/55">
                        <span className="font-display font-black text-fa-yellow text-base">1</span>
                        <ImageSquare size={10} weight="fill" />
                        Screenshot or manual XI
                    </div>
                    <div className="flex items-center gap-1.5 text-white/55">
                        <span className="font-display font-black text-fa-yellow text-base">2</span>
                        <Robot size={10} weight="fill" />
                        Gemini Vision reads it
                    </div>
                    <div className="flex items-center gap-1.5 text-white/55">
                        <span className="font-display font-black text-fa-yellow text-base">3</span>
                        <Crown size={10} weight="fill" />
                        Tier chain + HOLD/SWITCH plan
                    </div>
                </div>
            </div>

            {/* ── Control bar: MD selector + mode toggle ──────────────────── */}
            <div className="flex items-center justify-between gap-3 border border-white/10 bg-[#0d0d0d] p-2 flex-wrap"
                 data-testid="ai-control-bar">
                <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Mode</span>
                    <button
                        type="button"
                        onClick={() => setMode("upload")}
                        data-testid="ai-mode-upload"
                        className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 transition-all ${
                            mode === "upload"
                                ? "bg-fa-yellow text-black"
                                : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"
                        }`}
                    >
                        <ImageSquare size={11} weight={mode === "upload" ? "fill" : "regular"} />
                        Screenshot
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("manual")}
                        data-testid="ai-mode-manual"
                        className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 transition-all ${
                            mode === "manual"
                                ? "bg-fa-yellow text-black"
                                : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"
                        }`}
                    >
                        <PencilSimpleLine size={11} weight={mode === "manual" ? "fill" : "regular"} />
                        Manual entry
                    </button>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Matchday</span>
                    {[1, 2, 3].map((m) => (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setMatchday(m)}
                            data-testid={`ai-md-${m}`}
                            className={`font-mono text-[11px] font-bold tracking-widest w-8 h-7 transition-all ${
                                matchday === m
                                    ? "bg-fa-yellow text-black"
                                    : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"
                            }`}
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>

            {mode === "upload" && !imagePreview && (
                <label
                    htmlFor="ai-image-input"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.dataset.over = "1"; }}
                    onDragLeave={(e) => { e.currentTarget.dataset.over = "0"; }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.dataset.over = "0";
                        handleFile(e.dataTransfer.files?.[0]);
                    }}
                    className="block border-2 border-dashed border-fa-yellow/30 hover:border-fa-yellow data-[over=1]:border-fa-yellow data-[over=1]:bg-fa-yellow/10 bg-[#0d0d0d] p-8 md:p-10 text-center cursor-pointer transition-all group"
                    data-testid="ai-upload-dropzone"
                    data-over="0"
                >
                    <div className="inline-block p-3 rounded-full bg-fa-yellow/10 group-hover:bg-fa-yellow/20 mb-3 transition-colors">
                        <UploadSimple size={32} weight="bold" className="text-fa-yellow" />
                    </div>
                    <div className="font-display font-black text-lg uppercase tracking-tight text-white">
                        Drop screenshot here
                    </div>
                    <div className="font-mono text-[11px] text-white/50 mt-1">
                        or click to choose a file
                    </div>
                    <div className="flex items-center justify-center gap-1.5 mt-3 flex-wrap">
                        {["PNG", "JPEG", "WEBP"].map((f) => (
                            <span key={f}
                                  className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-white/[0.04] border border-white/10 text-white/55">
                                {f}
                            </span>
                        ))}
                        <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 text-white/40">
                            · max 8 MB
                        </span>
                    </div>
                    <div className="font-mono text-[10px] text-white/35 mt-3 flex items-center justify-center gap-1.5">
                        <Info size={10} weight="bold" />
                        Best results: FIFA Fantasy mobile app &rarr; My Team screen
                    </div>
                    <input
                        ref={fileInputRef}
                        id="ai-image-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        className="hidden"
                        data-testid="ai-image-input"
                    />
                </label>
            )}

            {mode === "upload" && imagePreview && (
                <div className="border border-fa-mint/30 bg-[#0d0d0d] p-3"
                     data-testid="ai-image-preview">
                    <div className="flex items-center justify-between mb-2">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5">
                            <CheckCircle size={11} weight="fill" /> Screenshot loaded · analyzing
                        </div>
                        <button
                            type="button"
                            onClick={reset}
                            data-testid="ai-image-clear"
                            className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white border border-white/15 px-2 py-0.5 flex items-center gap-1"
                        >
                            <X size={10} weight="bold" /> Clear
                        </button>
                    </div>
                    <img src={imagePreview} alt="Lineup screenshot" className="max-h-72 mx-auto" />
                </div>
            )}

            {mode === "manual" && (
                <ManualLineupEntry onSubmit={handleManual} busy={busy} />
            )}

            {busy && (
                <div className="border border-fa-yellow/30 bg-fa-yellow/[0.04] p-4 space-y-2"
                     data-testid="ai-loading">
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-fa-yellow">
                        <Spinner size={14} weight="bold" className="animate-spin" />
                        Analyzing your team…
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-widest">
                        <div className="flex items-center gap-1.5 text-fa-yellow">
                            <Spinner size={10} weight="bold" className="animate-spin" />
                            {mode === "upload" ? "Reading screenshot" : "Resolving players"}
                        </div>
                        <div className="flex items-center gap-1.5 text-white/40">
                            <span className="w-2 h-2 bg-white/20" />
                            Matching WC pool
                        </div>
                        <div className="flex items-center gap-1.5 text-white/40">
                            <span className="w-2 h-2 bg-white/20" />
                            Building captain chain
                        </div>
                    </div>
                </div>
            )}

            {result && !result.ok && (
                <div className="border border-red-500/30 bg-red-500/5 p-3 font-mono text-[11px] text-red-300"
                     data-testid="ai-result-empty">
                    {result.reason || "No players could be matched against the WC Fantasy database."}
                </div>
            )}

            {result?.ok && result.analysis && (
                <div className="space-y-4" data-testid="ai-result">
                    {/* HERO — primary captain spotlight */}
                    <ChainHero
                        chain={result.analysis.chain || []}
                        recommendations={result.analysis.recommendations}
                        userPicks={result.analysis.user_picks}
                    />

                    {/* Strategy explainer */}
                    <ChainStrategyBanner />

                    {/* Recommendations strip (compact, 3 picks) */}
                    <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="ai-recommendations">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5 mb-2">
                            <Trophy size={11} weight="fill" className="text-fa-yellow" /> Three captain windows
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 font-mono text-[11px]">
                            <div className="border-l-2 border-fa-yellow pl-2">
                                <div className="text-white/40 uppercase tracking-widest text-[9px]">First kickoff</div>
                                <div className="font-display font-black text-sm text-fa-yellow">
                                    {result.analysis.recommendations?.best_first || "—"}
                                </div>
                                <div className="text-white/40 text-[9px]">Default armband at MD start</div>
                            </div>
                            <div className="border-l-2 border-fa-mint pl-2">
                                <div className="text-white/40 uppercase tracking-widest text-[9px]">Best overall</div>
                                <div className="font-display font-black text-sm text-fa-mint">
                                    {result.analysis.recommendations?.best_overall || "—"}
                                </div>
                                <div className="text-white/40 text-[9px]">Highest ceiling in your XI</div>
                            </div>
                            <div className="border-l-2 border-fa-sky pl-2">
                                <div className="text-white/40 uppercase tracking-widest text-[9px]">Late swing</div>
                                <div className="font-display font-black text-sm text-fa-sky">
                                    {result.analysis.recommendations?.best_late_swing || "—"}
                                </div>
                                <div className="text-white/40 text-[9px]">Final fallback if chain blanks</div>
                            </div>
                        </div>
                    </div>

                    {/* Section header for chain */}
                    <div className="flex items-center justify-between pt-2">
                        <h3 className="font-display font-black text-base uppercase tracking-tight flex items-center gap-2">
                            <Crown size={16} weight="fill" className="text-fa-yellow" />
                            Your captain chain — MD{result.matchday}
                        </h3>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                            ordered by kickoff
                        </span>
                    </div>

                    {/* Chain timeline */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3"
                         data-testid="ai-chain-grid">
                        {(result.analysis.chain || []).map((row, i, arr) => (
                            <ChainTile
                                key={i}
                                row={row}
                                idx={i}
                                total={arr.length}
                                nextRow={arr[i + 1]}
                            />
                        ))}
                    </div>

                    {/* Squad-wide audit (all 11) */}
                    {result.audit && <SquadAuditPanel audit={result.audit} />}

                    {result.analysis.skipped?.length > 0 && (
                        <div className="border border-white/10 bg-white/[0.02] p-2 space-y-1"
                             data-testid="ai-skipped">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                Skipped (not viable captains)
                            </div>
                            {result.analysis.skipped.map((s, i) => (
                                <div key={i} className="font-mono text-[10px] text-white/50">
                                    <span className="text-white/70">{s.player}</span> — {s.reason}
                                </div>
                            ))}
                        </div>
                    )}

                    {result.mapping?.unmatched?.length > 0 && (
                        <div className="border border-red-400/30 bg-red-400/5 p-2 space-y-1"
                             data-testid="ai-unmatched">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-red-300">
                                Could not match {result.mapping.unmatched.length} player(s)
                            </div>
                            {result.mapping.unmatched.slice(0, 6).map((u, i) => (
                                <div key={i} className="font-mono text-[10px] text-white/50">
                                    <span className="text-white/70">{u.detected?.name || "—"}</span>
                                    {u.best_guess && <span className="text-white/30"> · best guess: {u.best_guess} ({u.score})</span>}
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={reset}
                        data-testid="ai-reset"
                        className="font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white border border-white/15 px-3 py-1.5"
                    >
                        Start over
                    </button>
                </div>
            )}
        </div>
    );
}
